import { useEffect } from "react";

import { getUser } from "@jti/rest-api";
import { useDispatch, useSelector } from "@jti/store/reduxHooks";
import { useGetLoyaltyTiersQuery } from "@jti/store/slices/loyaltyApiSlice";
import { fetchUserLoyaltyProfile, Status } from "@jti/store/slices/loyaltyCoreSlice";
import { type RootStoreState } from "@jti/store/types";
import { type CartOverviewTierNextTier, LoyaltyTier } from "@jti/ui/components/CartOverviewTier";
import { type TiersNames } from "@jti/ui/components/TierPointsProgressBar";

export type CartOverviewTierScenario = "upgrade" | "retention" | "downgrade" | "downgradeRetained" | "max";

type UseCartOverviewTierLogicResult = {
  isHidden: boolean;
  isLoading: boolean;
  isFailed: boolean;
  scenario: CartOverviewTierScenario;
  nextTier?: CartOverviewTierNextTier;
  userTierName: string;
  cumulativePoints: number;
  pointsToMaintainCurrentTier: number;
  pointsToMaintainCurrentTierWithPurchase: number;
  pointsToNextTier: number;
  pointsToNextTierWithPurchase: number;
  pointsForGoldTier?: number;
  pointsForBlackTier?: number;
  tiersNames: TiersNames;
};

export const useCartOverviewTier = (cartPoints: number): UseCartOverviewTierLogicResult => {
  const dispatch = useDispatch();

  const { uuid, ciam_token: ciamToken } = getUser();
  const isHidden = !(uuid && ciamToken);
  const {
    data: tiersThresholds = [],
    isLoading: tiersAreLoading,
    isError: tiersRequestFailed,
  } = useGetLoyaltyTiersQuery(undefined, { skip: isHidden });

  const tier = useSelector((state: RootStoreState) => state.loyaltyCore.tier);
  const pointsStatus = useSelector((state: RootStoreState) => state.loyaltyCore.points.status);
  const isLoyaltyProfileLoading = pointsStatus !== Status.COMPLETED && pointsStatus !== Status.FAILED;
  const isLoyaltyProfileFailed = pointsStatus === Status.FAILED;
  const isLoading = tiersAreLoading || isLoyaltyProfileLoading;
  const isFailed = isLoyaltyProfileFailed || tiersRequestFailed;

  useEffect(() => {
    if (!isHidden) {
      dispatch(fetchUserLoyaltyProfile({}));
    }
  }, [dispatch, isHidden]);

  const nextTierName = tier.next_tier_name?.toLowerCase();
  const apiNextTier: CartOverviewTierNextTier | undefined =
    nextTierName === LoyaltyTier.Gold || nextTierName === LoyaltyTier.Black ? nextTierName : undefined;

  const blackTierThreshold = tiersThresholds[2]?.condition_value;
  const userTierLower = (tier.name || LoyaltyTier.Silver).toLowerCase();

  const purchaseReachesBlackTier =
    blackTierThreshold !== undefined &&
    userTierLower !== LoyaltyTier.Black &&
    tier.cumulative_points + cartPoints >= blackTierThreshold;

  const nextTier: CartOverviewTierNextTier | undefined = purchaseReachesBlackTier ? LoyaltyTier.Black : apiNextTier;
  const pointsToNextTier = purchaseReachesBlackTier
    ? Math.max(blackTierThreshold - tier.cumulative_points, 0)
    : tier.points_to_next_tier;
  const pointsToNextTierWithPurchase = Math.max(pointsToNextTier - cartPoints, 0);

  const pointsToMaintainCurrentTier = tier.points_to_maintain_current_tier;
  const pointsToMaintainCurrentTierWithPurchase = Math.max(tier.points_to_maintain_current_tier - cartPoints, 0);

  const purchaseReachesNextTier =
    (Boolean(tier.next_tier_name) && cartPoints >= tier.points_to_next_tier) || purchaseReachesBlackTier;

  const getScenario = (): CartOverviewTierScenario => {
    if (purchaseReachesNextTier) {
      return "upgrade";
    }
    if (pointsToMaintainCurrentTier > 0) {
      if (pointsToMaintainCurrentTierWithPurchase === 0) {
        return "downgradeRetained";
      }

      return "downgrade";
    }
    if (!tier.next_tier_name) {
      return "max";
    }
    return "retention";
  };

  const tiersNames: TiersNames = {
    [LoyaltyTier.Silver]: tiersThresholds[0]?.name,
    [LoyaltyTier.Gold]: tiersThresholds[1]?.name,
    [LoyaltyTier.Black]: tiersThresholds[2]?.name,
  };

  return {
    isHidden,
    isLoading,
    isFailed,
    scenario: getScenario(),
    nextTier,
    userTierName: tier.name || LoyaltyTier.Silver,
    cumulativePoints: tier.cumulative_points,
    pointsToMaintainCurrentTier,
    pointsToMaintainCurrentTierWithPurchase,
    pointsToNextTier,
    pointsToNextTierWithPurchase,
    pointsForGoldTier: tiersThresholds[1]?.condition_value,
    pointsForBlackTier: blackTierThreshold,
    tiersNames,
  };
};
